import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import FilterEditorPopover from 'data-frames/filters/components/FilterEditorPopover';
import {DataFrameFilterOptions, FilterWithMetadata} from 'data-frames/filters/DataFrameFilterOptions';
import {DataFrameReadOnlyInfo} from 'data-frames/header-menu/DataFrameReadOnlyInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

import FilterPill from './FilterPill';

function AddFilterLink({
  filterOptions,
  additionalFilters,
  readOnlyInfo,
}: FilterPillListProps): JSX.Element | null {
  // If the last filter step already has a filter for every column, we will not show the add filter link
  if (
    readOnlyInfo.isReadOnly ||
    filterOptions.getUnfilteredColumns(filterOptions.dataFrameSpec).length === 0
  ) {
    return null;
  }

  return (
    <FilterEditorPopover filterOptions={filterOptions} floatingPlacement={FloatingPlacement.BOTTOM_START}>
      {(openMenu) => (
        <Tooltip
          disabled={readOnlyInfo.allUpdatesDisabledMessage == null}
          title={readOnlyInfo.allUpdatesDisabledMessage}
        >
          <Button
            buttonStyle={ButtonStyle.TEXT}
            className={css(styles.addFilterLink)}
            disabled={readOnlyInfo.allUpdatesDisabledMessage != null}
            icon="compose"
            onClick={openMenu}
            size={ButtonSize.SMALL}
            text="Add filter"
            dataTestAttrs={{component: 'AddFilterLink', element: 'button'}}
          />
        </Tooltip>
      )}
    </FilterEditorPopover>
  );
}

export interface AdditionalFilters {
  disabled: boolean;
  tooltipTitle?: string;
  filterWithMetadata: FilterWithMetadata;
}

export interface FilterPillListProps {
  /**
   * Filter options for the current data frame. These filters may be edited.
   */
  filterOptions: DataFrameFilterOptions;
  /**
   * Additional filters to display, which may be disabled and have an explanatory tooltip.
   */
  additionalFilters: Array<AdditionalFilters>;
  /**
   * Information about if all transforms are in read only mode, or disabled
   */
  readOnlyInfo: DataFrameReadOnlyInfo;
}

export default function FilterPillList({
  filterOptions,
  additionalFilters,
  readOnlyInfo,
}: FilterPillListProps): JSX.Element | null {
  if (additionalFilters.length === 0 && readOnlyInfo.isReadOnly) {
    return null;
  }
  return (
    <div
      className={classNames('scrollbar-override', css(styles.container))}
      {...getTestProps({component: 'FilterPillList', element: 'root'})}
    >
      {(additionalFilters ?? []).map(({filterWithMetadata, tooltipTitle, disabled}, filterIndex) => (
        <div className={css(styles.pill)} key={`filter-${filterIndex}`}>
          <TooltipWrapper tooltipTitle={tooltipTitle} tooltipPlacement={TooltipPlacement.TOP}>
            <FilterPill
              disabled={disabled}
              filterWithMetadata={filterWithMetadata}
              filterOptions={{
                updateFilterForColumn: (columnSystemName, newFilter) =>
                  filterOptions.updateFilterForColumn(
                    columnSystemName,
                    newFilter,
                    filterWithMetadata.filterId
                  ),
                dataFrameSpec: filterWithMetadata.dataFrameSpec,
                getFilterForColumnHeaders: (columnSystemName) =>
                  filterOptions.getFilterForColumn(columnSystemName, filterWithMetadata.filterId),
                getUnfilteredColumns: filterOptions.getUnfilteredColumns,
              }}
              readOnlyInfo={filterWithMetadata.readOnlyInfo}
              onDelete={() =>
                filterOptions.updateFilterForColumn(
                  filterWithMetadata.filter.filters[0].columnSystemName,
                  null,
                  filterWithMetadata.filterId
                )
              }
            />
          </TooltipWrapper>
        </div>
      ))}
      <div className={css(styles.addFilterLinkWrapper)}>
        <AddFilterLink
          filterOptions={filterOptions}
          readOnlyInfo={readOnlyInfo}
          additionalFilters={additionalFilters}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  addFilterLink: {
    paddingLeft: 2,
  },
  addFilterLinkWrapper: {
    flexShrink: 0,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: 4,
    overflowX: 'auto',
  },
  pill: {
    marginRight: 8,
  },
});
